import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Button from "@mui/material/Button";

// Components
import CustomButton from "../../../../../components/CustomButton";

// Styles
import stepsStyles from "../steps.module.scss";
import styles from "./style.module.scss";

// Types
import { CourseData } from "../../types";
import { useCoursesNewContext } from "../../context";

interface SourcesProps {
  onNext: (courseData: CourseData | null) => void;
  onPrev: () => void;
}

const Sources: React.FC<SourcesProps> = ({ onNext, onPrev }) => {
  // Translations
  const { t } = useTranslation();

  // Context
  const { course, updateCourse } = useCoursesNewContext();

  // State
  const [sources, setSources] = useState<string[]>([]);

  const options = useMemo(() => [
    {
      label: t('courses.new.sources.options.primary'),
      value: 'primary',
    },
    {
      label: t('courses.new.sources.options.secondary'),
      value: 'secondary',
    },
    {
      label: t('courses.new.sources.options.tertiary'),
      value: 'tertiary',
    },
    {
      label: t('courses.new.sources.options.verified'),
      value: 'verified',
    },
    {
      label: t('courses.new.sources.options.unverified'),
      value: 'unverified',
    },
    {
      label: t('courses.new.sources.options.partially_reliable'),
      value: 'partially_reliable',
    },
    {
      label: t('courses.new.sources.options.all'),
      value: 'all',
    },
  ], [t]);

  const hasAllSources = useMemo(() => sources.length === options.length - 1, [sources, options]);

  // Handlers
  const handleChangeSources = useCallback((source: string) => {
    if (source === 'all') {
      if (hasAllSources) {
        setSources([]);
      } else {
        setSources(['primary', 'secondary', 'tertiary', 'verified', 'unverified', 'partially_reliable']);
      }
      return;
    }

    setSources((prev) => {
      if (prev.includes(source)) {
        return prev.filter((s) => s !== source);
      }
      return [...prev, source];
    });
  }, [hasAllSources]);

  const handleNext = useCallback(async () => {
    if (sources.length === 0) {
      return;
    }

    const newCourse = { ...course, sources };
    if (course?.id) {
      await updateCourse(newCourse);
    }
    onNext(newCourse);
  }, [sources, onNext]);

  const handlePrev = useCallback(() => {
    onPrev();
  }, [onPrev]);

  // Render
  return (
    <div className={stepsStyles.root}>
      <div className={stepsStyles.content}>
        <h1 className='text-2xl font-bold text-black dark:text-white'>
          {t('courses.new.sources.title')}
        </h1>

        <div className={styles.options}>
          {options.map((option) => {
            const isActive = sources.includes(option.value) || (option.value === 'all' && hasAllSources);
            return (
              <Button
                key={option.value}
                variant={isActive ? 'outlined' : 'text'}
                color="primary"
                size="large"
                classes={{
                  root: `${styles.option} ${isActive ? styles.option_active : ''}`,
                  text: styles.option_text,
                }}
                onClick={() => handleChangeSources(option.value)}
              >
                {option.label}
              </Button>
            )
          })}
        </div>
      </div>

      <div className={stepsStyles.actions}>
        <CustomButton
          variant='secondary'
          onClick={handlePrev}
          classes={{ root: stepsStyles.prevButton }}
        >
          {t('actions.previous')}
        </CustomButton>

        <CustomButton
          variant='primary'
          onClick={handleNext}
          disabled={sources.length === 0}
          classes={{ root: stepsStyles.nextButton }}
        >
          {t('actions.next')}
        </CustomButton>
      </div>
    </div >
  )
};

export default Sources;
